import { checkHasSpecialExceptionStatus } from '@/components/ReportForm/ApReportFormInputSpecialExceptionStatusSelect/utils';
import { useGetPopupContainer } from '@/components/ReportForm/Container/PopupContainer';
import { InputBase, LabelProps } from '@/components/ReportForm/InputBase';
import {
  InputBaseType,
  SelectType,
} from '@/components/ReportForm/InputBase/types';
import {
  SpecialExceptionStatusAddressInput,
  SpecialExceptionStatusSelect,
} from '@/components/SpecialExceptionStatusSelect';
import { useTranslation } from '@/i18n';
import { FormSelectProps } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { memo, useCallback, useMemo } from 'react';
import { FieldPath, FieldValues, useWatch } from 'react-hook-form';
import styles from './apReportFormInputSpecialExceptionStatusSelect.module.scss';

const cx = classnames.bind(styles);

export type Props<TFieldValues extends FieldValues> =
  InputBaseType<TFieldValues> & {
    subInputName: FieldPath<TFieldValues>;
    onChangeAfter?: (value: boolean) => void;
  } & SelectType;

const InnerApReportFormInputSpecialExceptionStatusSelect = <
  TFieldValues extends FieldValues
>({
  control,
  name,
  inputId,
  label,
  tooltip,
  hidden,
  caption,
  defaultOptions,
  subInputName,
  required = false,
  onChangeAfter,
}: Props<TFieldValues>) => {
  const labelProps: LabelProps = useMemo(
    () => ({ required, children: label, tooltip }),
    [required, label, tooltip]
  );
  const specialExceptionStatusId = useWatch({ name, control });
  const hasSpecialExceptionStatusId = useMemo(() => {
    return checkHasSpecialExceptionStatus(specialExceptionStatusId);
  }, [specialExceptionStatusId]);
  const { t } = useTranslation();
  const rules: { required: string | boolean } = useMemo(
    () => ({ required: required ? t('required') : false }),
    [required, t]
  );
  const containerRef = useGetPopupContainer();
  const memorizedSelectProps = useMemo(
    (): Omit<
      FormSelectProps<TFieldValues>['selectProps'],
      'options' | 'comboBox'
    > => ({
      getPopupContainer: containerRef,
    }),
    [containerRef]
  );

  const _onChangeAfter = useCallback(
    (value: string | number | (string | number)[]) => {
      return onChangeAfter?.(
        checkHasSpecialExceptionStatus(value ? String(value) : undefined)
      );
    },
    [onChangeAfter]
  );

  return (
    <InputBase labelProps={labelProps} description={caption} hidden={hidden}>
      <div className={cx(styles['container'])}>
        <SpecialExceptionStatusSelect<TFieldValues>
          defaultOptions={defaultOptions}
          control={control}
          name={name}
          inputId={inputId}
          rules={rules}
          selectProps={memorizedSelectProps}
          onChangeAfter={_onChangeAfter}
        />
        {hasSpecialExceptionStatusId && (
          <SpecialExceptionStatusAddressInput<TFieldValues>
            control={control}
            inputId={`${inputId}_address`}
            name={subInputName}
          />
        )}
      </div>
    </InputBase>
  );
};

InnerApReportFormInputSpecialExceptionStatusSelect.displayName =
  'InnerApReportFormInputSpecialExceptionStatusSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ApReportFormInputSpecialExceptionStatusSelect = memo((props) => (
  <InnerApReportFormInputSpecialExceptionStatusSelect {...props} />
)) as typeof InnerApReportFormInputSpecialExceptionStatusSelect;

ApReportFormInputSpecialExceptionStatusSelect.displayName =
  'ApReportFormInputSpecialExceptionStatusSelect';
